import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, CheckCircle, Clock, Mail } from 'lucide-react';
import SITE_CONFIG from '../config/siteConfig';

const RefundPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Refund Policy - AI Carousel Maker</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/refund-policy`} />
        <meta name="description" content="Refund Policy for AI Carousel Maker. Learn about our refund terms, eligibility criteria, and process for credit purchases." />
        <meta property="og:title" content="Refund Policy - AI Carousel Maker" />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/refund-policy`} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Refund Policy
          </h1>
          <p className="text-xl text-gray-600">
            Last updated: December 16, 2025
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            
            {/* Overview */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 leading-relaxed">
                At AI Carousels by Novialcraft, we strive to provide the best service possible. 
                This Refund Policy outlines the circumstances under which refunds may be issued 
                for credit purchases on our platform.
              </p>
            </div>

            {/* Credit Purchases */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Credit Purchases</h2>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">
                    All credit purchases are <strong>final and non-refundable</strong> once the 
                    transaction is completed and credits are added to your account.
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Credits are digital goods that are delivered instantly upon purchase. Once credits 
                are added to your account, they cannot be refunded, exchanged, or transferred to 
                another account.
              </p>
            </div>

            {/* Exceptions */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Exceptions</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may consider refund requests in the following exceptional circumstances:
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Technical Issues</h3>
                    <p className="text-gray-700">
                      If you were charged but did not receive your credits due to a technical error 
                      on our platform.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Duplicate Charges</h3>
                    <p className="text-gray-700">
                      If you were accidentally charged multiple times for the same purchase.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Unauthorized Charges</h3>
                    <p className="text-gray-700">
                      If you believe there was an unauthorized charge on your account.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Refund Process */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Request Process</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you believe you qualify for a refund under the exceptions listed above, 
                please follow these steps:
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-700">
                <li className="leading-relaxed">
                  <strong>Contact Support:</strong> Email us at{' '}
                  <a href="mailto:support@novialtechnologies.com" className="text-purple-600 hover:text-purple-700">
                    support@novialtechnologies.com
                  </a>{' '}
                  within 7 days of the purchase.
                </li>
                <li className="leading-relaxed">
                  <strong>Provide Details:</strong> Include your transaction ID, purchase date, 
                  amount charged, and a detailed explanation of the issue.
                </li>
                <li className="leading-relaxed">
                  <strong>Wait for Review:</strong> Our team will review your request within 
                  3-5 business days.
                </li>
                <li className="leading-relaxed">
                  <strong>Receive Decision:</strong> You will be notified via email about the 
                  outcome of your refund request.
                </li>
              </ol>
            </div>

            {/* Processing Time */}
            <div className="mb-8">
              <div className="bg-purple-50 rounded-lg p-6">
                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-purple-600 mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Refund Processing Time
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      If your refund request is approved, the refund will be processed within 
                      7-10 business days. The refund will be credited to the original payment 
                      method used for the purchase. Please note that it may take additional time 
                      for the refund to appear in your account, depending on your bank or payment 
                      provider.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Issues */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Issues</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you experience issues with our AI generation services (such as failed image 
                generation or poor quality results), we will:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li className="leading-relaxed">
                  Investigate the issue and attempt to resolve it
                </li>
                <li className="leading-relaxed">
                  Refund the credits used for the failed generation if the issue was on our end
                </li>
                <li className="leading-relaxed">
                  Provide technical support to help you achieve better results
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Please note that subjective dissatisfaction with generated content does not 
                qualify for a refund, as AI-generated results can vary based on input and 
                creative interpretation.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-purple-600 mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Questions About Refunds?
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      If you have any questions about our refund policy or need assistance with 
                      a refund request, please contact us:
                    </p>
                    <p className="text-gray-700">
                      <strong>Email:</strong>{' '}
                      <a href="mailto:support@novialtechnologies.com" className="text-purple-600 hover:text-purple-700">
                        support@novialtechnologies.com
                      </a>
                    </p>
                    <p className="text-gray-700 mt-2">
                      <strong>Response Time:</strong> Within 24-48 hours
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Changes to Policy */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify this Refund Policy at any time. Any changes will 
                be effective immediately upon posting on this page. Your continued use of our 
                services after any changes constitutes acceptance of the updated policy.
              </p>
            </div>

            {/* Footer Note */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                This Refund Policy is part of our{' '}
                <a href="/terms-and-conditions" className="text-purple-600 hover:text-purple-700">
                  Terms and Conditions
                </a>
                . By using AI Carousels, you agree to this Refund Policy.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default RefundPolicyPage;
